<script setup lang="ts">
/**
 * „Vorgangskarte“: one work item — an activity, a ticket, an approval — as a
 * card in a list, in the bell dropdown or inside a chat message.
 *
 * Left an accent strip (priority: `tone` / `hue`, or any colour token as
 * `accent`) and a tile with the type icon (`#icon`, tinted by `iconTone` /
 * `iconHue`, else by the card's accent). `accentLevel` sets how much of the
 * accent shows (`NxAccentLevel`: the tile only, strip and tile, or both plus
 * a wash, or `muted`), `accentEmphasis` its weight, `opacity` fades the card. In the head line the
 * title, its badges (`badges` or `#badges`: the reason in its category hue,
 * the status) and on the right the reference and the due date — `overdue`
 * turns it red and adds an alert icon. Below: the meta line (`meta` or
 * `#meta`, „Von → An · Quelle · Pool“), the text (`text` or the default
 * slot), a document chip (`#document`) and the actions (`#actions`).
 * `#media` holds a picture that belongs to the item — a link preview's page
 * image, a thumbnail: under the text (default) or, with `mediaPlacement="end"`,
 * beside the text at the end of the card, at most 40 % wide. The card sets
 * no size on it: the slot content (an `NxMediaTile`, an `<img>`) brings its own.
 * `#footer` spans the whole card under tile and text — controls that need
 * the full width, e.g. a device's settings. `chevron` adds a trailing „›“
 * to the head row: the card opens something (a device, a record).
 *
 * Pointer and keyboard follow `NxBoard`'s cards: a click selects (`select`),
 * a double click opens (`open`); the card is a tab stop, Space selects and
 * Enter opens. Clicks and keys on controls inside the card — the actions, the
 * document chip — stay with those controls. `interactive=false` turns it into
 * a plain display, e.g. inside a chat message whose buttons do the work.
 *
 * `compact` is the small variant for bell entries and chat embeds. The card
 * holds no state of its own: `selected` and `unread` come from the caller.
 * So does `attention` (I-2999): a brand outline round the card for the moment
 * after a jump has landed on it — the caller sets it and takes it back again.
 *
 * Expandable (I-2382, kit request K1): with `expandable` the title becomes a
 * disclosure button (`aria-expanded`, a leading chevron that turns — hide it
 * with `expandChevron=false`) bound to `v-model:expanded`; `alwaysOpen` pins
 * the card open with a plain title (a timeline card then lies flat, without
 * the open shadow). While open, `#detail` renders (only then,
 * so its fetches stay lazy) and the meta line and text step aside; a click
 * while text inside the card is selected neither selects nor toggles. Rows:
 * `#eyebrow` above the title (it gives way first when the row gets tight),
 * `#aside` at the end of the head row — or of the eyebrow row with
 * `asidePlacement="eyebrow"` — and `#head-actions` beside it, revealed on
 * hover, focus, open or selected (always on touch; `headActionsVisible`
 * forces it; `headActionsSize="compact"` keeps an icon-only `xs` button from
 * growing the row); `#chips` a full-width row under the text; `#title` a rich title
 * (the `title` prop stays its accessible text). `focusable=false` keeps the
 * card clickable without a tab stop of its own, for lists that move the
 * selection with the arrow keys. `look` picks the surface: `card` (default),
 * `timeline` (no frame at rest, the strip turns brand on hover, open and
 * selected) or `list` (framed, the strip from the accent, a one-line title);
 * both take the small title and show the selection like every kit card: a
 * brand edge and the subtle brand fill (owner 12.10., one brand colour); a list
 * card with an accent shows it in the accent's colour instead (edge and a wash).
 * `tile` centres the icon, name, badges and meta in a quiet grid entry. A
 * single-line name keeps the full text in `NxTooltip` below the tile after
 * 800 ms of hover or keyboard focus. Inside `NxTileGrid` it is a list item;
 * selection, opening and keyboard controls keep the same contract.
 * An app region whose clicks stay its own carries `data-nx-ignore-click`.
 */
import { computed, inject, useId, useSlots, useTemplateRef } from 'vue';
import { AlertIcon, ChevronRightIcon } from '@blpde/nx-icons';
import NxBadge, { type NxBadgeVariant } from '../Badge/NxBadge.vue';
import type { NxHue } from '../lib/hue';
import NxTooltip from '../Tooltip/Tooltip.vue';
import NxCheckbox from '../Inputs/NxCheckbox.vue';
import { tileGridKey } from '../Tile/context';
import { useNativeDropTarget } from '../FileDrop/useNativeDropTarget';
import {
  accentVars,
  opacityStyle,
  resolveAccent,
  type NxAccentLevel,
  type NxAccentTone,
} from '../Panel/accent';

/** The surface of the card: see the component comment. */
export type NxItemCardLook = 'card' | 'timeline' | 'list' | 'tile';

/** One badge next to the title. */
export interface NxItemCardBadge {
  label: string;
  /** Category hue, e.g. the reason („Freigabe“ in violet). Wins over `variant`. */
  hue?: NxHue;
  /** Intent colour, e.g. the status. Defaults to `neutral`. */
  variant?: NxBadgeVariant;
}

const props = withDefaults(
  defineProps<{
    title: string;
    /** Body text, clamped to two lines (the default slot replaces it unclamped). */
    text?: string;
    /** Meta line; an array is joined with „ · “. */
    meta?: string | readonly string[];
    /** Number of the item, e.g. „A-2026-0479“, shown in monospace at the top right. */
    reference?: string;
    /** Due date, already formatted by the caller („heute 16:00“). */
    due?: string;
    /** Marks `due` as overdue: red, bold, alert icon and `overdueLabel` for screen readers. */
    overdue?: boolean;
    badges?: readonly NxItemCardBadge[];
    /** Accent strip on the left in an intent tone, e.g. the priority. */
    tone?: NxAccentTone;
    /** Accent strip in a category hue instead. Wins over `tone`. */
    hue?: NxHue;
    /**
     * The accent from any colour token, named without `--nxt-` (`'warning-bg'`,
     * `'hue-teal'`) — colours a document picks, e.g. a presentation layer.
     * Wins over `hue` and `tone`; not a token name → ignored.
     */
    accent?: string | null;
    /**
     * How much of the accent shows: `edge` (default) strip and tile, `fill`
     * also a light wash over the card, `tint` the tile only, `muted` no
     * accent and the card steps back (muted text, less saturation).
     */
    accentLevel?: NxAccentLevel;
    /** 0–1: the strip's width (0 = none) and a token accent's strength. Default 1. */
    accentEmphasis?: number;
    /** 0–1: fades the whole card, e.g. items a presentation layer pushes back. */
    opacity?: number;
    /** Tint of the icon tile in an intent tone. */
    iconTone?: NxAccentTone;
    /** Tint of the icon tile in a category hue, e.g. the item type. Wins over `iconTone`. */
    iconHue?: NxHue;
    unread?: boolean;
    selected?: boolean;
    /** Show a separate checkbox for additive selection without opening the card. */
    selectable?: boolean;
    /** Accessible name of that checkbox; defaults to the title. */
    selectionLabel?: string;
    /**
     * A hint for the eye that the user was just taken to this card (a jump from a
     * link, a search hit): a brand outline round it that fades in from transparent —
     * with no transition under reduced motion. The caller sets it and takes it back
     * after a moment; the card keeps no timer. Not a selection, and no change for
     * assistive technology.
     */
    attention?: boolean;
    compact?: boolean;
    /** `false`: no tab stop, no `select` / `open` — a plain display. */
    interactive?: boolean;
    /**
     * A trailing „›“ at the end of the head row (beside title, badges and
     * aside): the card opens something. Decorative — the card is the control.
     */
    chevron?: boolean;
    unreadLabel?: string;
    overdueLabel?: string;
    /** The title toggles `v-model:expanded`; `#detail` shows while open. */
    expandable?: boolean;
    /** Pinned open: detail shown, the title is plain text, nothing toggles. */
    alwaysOpen?: boolean;
    /** The leading chevron of an expandable title. */
    expandChevron?: boolean;
    /** `false`: clickable, but no tab stop of its own (the list owns focus). */
    focusable?: boolean;
    look?: NxItemCardLook;
    /** Where `#aside` and `#head-actions` sit: the head row or the eyebrow row. */
    asidePlacement?: 'head' | 'eyebrow';
    /** Show `#head-actions` without hover or focus, e.g. while a toggle is on. */
    headActionsVisible?: boolean;
    /**
     * `compact`: the `#head-actions` take no height of their own, so an icon-only
     * `NxButton height="xs"` (taller than the 22px eyebrow row) does not grow the
     * row — they stay centred on it and the row keeps the height of its other
     * content. Default `default`: the row grows with the actions.
     */
    headActionsSize?: 'default' | 'compact';
    /** Where `#media` sits: under the text, or beside it at the end of the card. */
    mediaPlacement?: 'below' | 'end';
    /** Consumer-owned native drag; `dragStart` fills dataTransfer. Off by default. */
    draggable?: boolean;
    /** Native drop target; rechecked on every dragover and drop. Omit to keep parent-area behaviour. */
    canDrop?: (event: DragEvent) => boolean;
    /**
     * Which drags the card takes part in, with `canDrop`. `'any'` (default): every drag — a
     * refused one is consumed. `'files'`: only drags that carry files; text, links and
     * in-app items are left alone, so they still reach the fields in `#detail` (a comment
     * textarea) and the surrounding area.
     */
    dropKind?: 'files' | 'any';
    /** Accessible feedback while an accepted drag is over the card. */
    dropLabel?: string;
  }>(),
  {
    text: undefined,
    meta: undefined,
    reference: undefined,
    due: undefined,
    overdue: false,
    badges: () => [],
    tone: undefined,
    hue: undefined,
    accent: undefined,
    accentLevel: 'edge',
    accentEmphasis: 1,
    opacity: 1,
    iconTone: undefined,
    iconHue: undefined,
    unread: false,
    selected: false,
    selectable: false,
    selectionLabel: undefined,
    attention: false,
    compact: false,
    interactive: true,
    chevron: false,
    unreadLabel: 'Ungelesen',
    overdueLabel: 'Überfällig',
    expandable: false,
    alwaysOpen: false,
    expandChevron: true,
    focusable: true,
    look: 'card',
    asidePlacement: 'head',
    headActionsVisible: false,
    headActionsSize: 'default',
    mediaPlacement: 'below',
    draggable: false,
    canDrop: undefined,
    dropKind: 'any',
    dropLabel: undefined,
  }
);

/** Open state of an expandable card (`v-model:expanded`). */
const expanded = defineModel<boolean>('expanded', { default: false });

const emit = defineEmits<{
  /** Click or Space on the card itself. Carries the event (modifier keys for multi-select). */
  select: [event: MouseEvent | KeyboardEvent];
  /** Checkbox toggle request; the owner handles additive/range selection. */
  toggleSelect: [event: MouseEvent];
  /** Double click or Enter on the card itself. */
  open: [event: MouseEvent | KeyboardEvent];
  dragStart: [event: DragEvent];
  dragEnd: [event: DragEvent];
  drop: [event: DragEvent];
}>();

defineSlots<{
  /** Type icon inside the tinted tile. Without it there is no tile. */
  icon?(): unknown;
  /** Replaces the `badges` prop. */
  badges?(): unknown;
  /** Replaces the `meta` prop, for rich content. */
  meta?(): unknown;
  /** Replaces the `text` prop, unclamped. */
  default?(): unknown;
  /** Document chip, e.g. „Auftrag 28870 · Pos. 2“. */
  document?(): unknown;
  /** Action buttons (primary, reject, …). */
  actions?(): unknown;
  /** Full-width region under the tile and the text (controls, a list). */
  footer?(): unknown;
  /** Rich title (search marks); the `title` prop stays the accessible text. */
  title?(): unknown;
  /** A caption row above the title, e.g. the item type. */
  eyebrow?(): unknown;
  /** App content at the end of the head row (or the eyebrow row). */
  aside?(): unknown;
  /** Actions beside `#aside`, revealed on hover, focus, open or selected. */
  'head-actions'?(): unknown;
  /** A picture of the item (a link preview's image, a thumbnail), under or beside the text. */
  media?(): unknown;
  /** A full-width row under the text, e.g. markers. */
  chips?(): unknown;
  /** The expanded content; rendered only while open. */
  detail?(): unknown;
}>();

const id = useId();
const root = useTemplateRef<HTMLElement>('root');
const dropTarget = useNativeDropTarget({
  enabled: () => Boolean(props.canDrop),
  accepts: (event) =>
    props.dropKind !== 'files' || Array.from(event.dataTransfer?.types ?? []).includes('Files'),
  key: () => 'card',
  canDrop: (event) => props.canDrop?.(event) ?? false,
  onDrop: (event) => emit('drop', event),
  label: () => props.dropLabel ?? props.title,
});
const slots = useSlots();
const inTileGrid = inject(tileGridKey, false);

const isOpen = computed(() => props.expandable && (props.alwaysOpen || expanded.value));
/** The title toggles only on an expandable card that is not pinned open. */
const isToggle = computed(() => props.expandable && !props.alwaysOpen);
const asideInEyebrow = computed(() => props.asidePlacement === 'eyebrow');
// Slot presence is not reactive: these are read on every render (a slot behind
// `v-if` in the caller may come and go), never cached in a computed.
function hasEyebrowRow(): boolean {
  return !!slots.eyebrow || (asideInEyebrow.value && (!!slots.aside || !!slots['head-actions']));
}
function hasHeadAside(): boolean {
  return (
    !!props.reference ||
    !!props.due ||
    (!asideInEyebrow.value && (!!slots.aside || !!slots['head-actions']))
  );
}

function toggle() {
  // Ending a text selection on the title (dragging over the subject) is not a toggle.
  if (isToggle.value && !selectsText()) expanded.value = !expanded.value;
}

/** A click that ends a text selection inside the card is not a click on the card. */
function selectsText(): boolean {
  const selection = typeof window === 'undefined' ? null : window.getSelection?.();
  if (!selection || selection.isCollapsed || !selection.toString()) return false;
  return !!selection.anchorNode && !!root.value?.contains(selection.anchorNode);
}

const metaText = computed(() =>
  Array.isArray(props.meta) ? props.meta.filter(Boolean).join(' · ') : (props.meta ?? '')
);

const accent = computed(() => resolveAccent(props, '--item-accent'));
const hasAccent = computed(() => {
  const { hasColor, level } = accent.value;
  return hasColor && (level === 'edge' || level === 'fill');
});
// The tile: its own tint first, else the card's accent (not when muted).
const iconVars = computed(() =>
  props.iconHue || props.iconTone
    ? accentVars(props.iconTone, props.iconHue, '--item-icon')
    : resolveAccent(props, '--item-icon').vars
);
const hasIconTint = computed(() => Object.keys(iconVars.value).length > 0);
// Read before the date: „Überfällig: heute 16:00“.
const overdueText = computed(() => `${props.overdueLabel}:`);
const style = computed(() => ({
  ...accent.value.vars,
  ...iconVars.value,
  ...opacityStyle(props.opacity),
}));

// Everything a click inside the card can mean for a control of its own.
const CONTROLS =
  'a[href], button, input, select, textarea, summary, label, [contenteditable]:not([contenteditable="false"]), [role="button"], [role="link"], [role="checkbox"], [role="switch"], [role="menuitem"], [role="option"], [role="tab"], [tabindex], [data-nx-ignore-click]';

/** True when the event started on a control inside the card (not on the card itself). */
function fromControl(event: Event): boolean {
  const target = event.target as Element | null;
  const hit = target?.closest?.(CONTROLS);
  return !!hit && hit !== root.value && !!root.value?.contains(hit);
}

function onClick(event: MouseEvent) {
  if (!props.interactive || fromControl(event)) return;
  if (props.expandable && selectsText()) return;
  emit('select', event);
}

function onDblclick(event: MouseEvent) {
  if (!props.interactive || fromControl(event)) return;
  emit('open', event);
}

function onKeydown(event: KeyboardEvent) {
  // Only keys on the focused card itself; a focused button inside handles its own.
  if (!props.interactive || event.target !== root.value) return;
  if (event.key === 'Enter') {
    event.preventDefault();
    emit('open', event);
  } else if (event.key === ' ' || event.key === 'Spacebar') {
    event.preventDefault();
    emit('select', event);
  }
}

let nativeDragStarted = false;
function onDragStart(event: DragEvent) {
  if (!props.draggable) return;
  if (!props.interactive || fromControl(event)) {
    event.preventDefault();
    return;
  }
  event.stopPropagation();
  emit('dragStart', event);
  nativeDragStarted = !event.defaultPrevented;
}

function onDragEnd(event: DragEvent) {
  dropTarget.reset();
  if (nativeDragStarted) emit('dragEnd', event);
  nativeDragStarted = false;
}
</script>

<template>
  <article
    ref="root"
    class="nx-item-card"
    :class="{
      'has-accent': hasAccent,
      'is-accent-fill': accent.hasColor && accent.level === 'fill',
      'is-muted': accent.level === 'muted',
      'is-selected': selected,
      'is-selectable': selectable,
      'is-unread': unread,
      'is-attention': attention,
      'is-compact': compact,
      'is-interactive': interactive,
      'is-expandable': expandable,
      'is-expanded': isOpen,
      'is-pinned': expandable && alwaysOpen,
      [`look-${look}`]: look !== 'card',
      'is-drop-target': dropTarget.isActive(true),
    }"
    :style="style"
    :role="inTileGrid ? 'listitem' : undefined"
    :tabindex="interactive && focusable ? 0 : undefined"
    :aria-labelledby="unread ? `${id}-unread ${id}-title` : `${id}-title`"
    :aria-describedby="
      [due ? `${id}-due` : '', !isOpen && (metaText || $slots.meta) ? `${id}-meta` : '']
        .filter(Boolean)
        .join(' ') || undefined
    "
    :aria-current="interactive && selected && !selectable ? 'true' : undefined"
    :draggable="draggable || undefined"
    :aria-description="dropTarget.isActive(true) ? (dropLabel ?? title) : undefined"
    @click="onClick"
    @dblclick="onDblclick"
    @keydown="onKeydown"
    @dragstart="onDragStart"
    @dragend="onDragEnd"
    @dragenter="dropTarget.dragenter($event, true)"
    @dragover="dropTarget.dragover($event, true)"
    @dragleave="dropTarget.dragleave"
    @drop="dropTarget.drop($event, true)"
  >
    <NxCheckbox
      v-if="selectable"
      class="nx-item-card__selection"
      :model-value="selected"
      :aria-label="selectionLabel ?? title"
      :disabled="!interactive"
      @click.stop="emit('toggleSelect', $event)"
    />
    <span
      v-if="$slots.icon"
      class="nx-item-card__tile"
      :class="{ 'is-tinted': hasIconTint }"
      aria-hidden="true"
    >
      <slot name="icon" />
    </span>

    <div class="nx-item-card__main">
      <!-- The eyebrow row: a caption above the title; the aside may join it. -->
      <div v-if="hasEyebrowRow()" class="nx-item-card__eyebrow-row">
        <span v-if="$slots.eyebrow" class="nx-item-card__eyebrow"><slot name="eyebrow" /></span>
        <span class="nx-item-card__spacer" />
        <template v-if="asideInEyebrow">
          <span
            v-if="$slots['head-actions']"
            class="nx-item-card__head-actions"
            :class="{
              'is-visible': headActionsVisible,
              'is-compact': headActionsSize === 'compact',
            }"
          >
            <slot name="head-actions" />
          </span>
          <span v-if="$slots.aside" class="nx-item-card__slot-aside"><slot name="aside" /></span>
        </template>
      </div>
      <!-- The head row: the head wraps inside it, the chevron stays at its end. -->
      <div class="nx-item-card__head-row">
        <div class="nx-item-card__head">
          <span v-if="unread" class="nx-item-card__unread">
            <span :id="`${id}-unread`" class="nx-item-card__sr">{{ unreadLabel }}</span>
          </span>
          <button
            v-if="isToggle"
            :id="`${id}-title`"
            type="button"
            class="nx-item-card__title nx-item-card__toggle"
            :aria-expanded="isOpen ? 'true' : 'false'"
            @click="toggle"
          >
            <ChevronRightIcon
              v-if="expandChevron"
              filled
              class="nx-item-card__toggle-chevron nxt-icon-sm"
              :class="{ 'is-open': isOpen }"
              aria-hidden="true"
            />
            <span class="nx-item-card__title-text">
              <template v-if="$slots.title">
                <!-- The `title` prop is the accessible name; the rich title is for the eye. -->
                <span class="nx-item-card__sr">{{ title }}</span>
                <span aria-hidden="true"><slot name="title" /></span>
              </template>
              <template v-else>{{ title }}</template>
            </span>
          </button>
          <span v-else :id="`${id}-title`" class="nx-item-card__title">
            <template v-if="$slots.title">
              <span class="nx-item-card__sr">{{ title }}</span>
              <span aria-hidden="true"><slot name="title" /></span>
            </template>
            <template v-else>{{ title }}</template>
          </span>
          <span v-if="badges.length || $slots.badges" class="nx-item-card__badges">
            <slot name="badges">
              <NxBadge
                v-for="(badge, index) in badges"
                :key="index"
                size="xs"
                :hue="badge.hue"
                :variant="badge.variant ?? 'neutral'"
              >
                {{ badge.label }}
              </NxBadge>
            </slot>
          </span>
          <span v-if="hasHeadAside()" class="nx-item-card__aside">
            <span v-if="reference" class="nx-item-card__reference">{{ reference }}</span>
            <span
              v-if="due"
              :id="`${id}-due`"
              class="nx-item-card__due"
              :class="{ 'is-overdue': overdue }"
            >
              <AlertIcon v-if="overdue" filled class="nx-item-card__due-icon" aria-hidden="true" />
              <span v-if="overdue" class="nx-item-card__sr">{{ overdueText }}</span>
              {{ due }}
            </span>
            <template v-if="!asideInEyebrow">
              <span
                v-if="$slots['head-actions']"
                class="nx-item-card__head-actions"
                :class="{
                  'is-visible': headActionsVisible,
                  'is-compact': headActionsSize === 'compact',
                }"
              >
                <slot name="head-actions" />
              </span>
              <slot name="aside" />
            </template>
          </span>
        </div>
        <ChevronRightIcon
          v-if="chevron"
          filled
          class="nx-item-card__chevron nxt-icon-sm"
          aria-hidden="true"
        />
      </div>

      <!-- An open expandable card shows its detail instead of the summary. -->
      <template v-if="!isOpen">
        <div v-if="metaText || $slots.meta" :id="`${id}-meta`" class="nx-item-card__meta">
          <slot name="meta">{{ metaText }}</slot>
        </div>

        <div v-if="$slots.default" class="nx-item-card__text">
          <slot />
        </div>
        <div v-else-if="text" class="nx-item-card__text is-clamped" :title="text">{{ text }}</div>
      </template>

      <div v-if="$slots.media && mediaPlacement === 'below'" class="nx-item-card__media">
        <slot name="media" />
      </div>

      <div v-if="$slots.chips" class="nx-item-card__chips">
        <slot name="chips" />
      </div>

      <div v-if="$slots.document" class="nx-item-card__document">
        <slot name="document" />
      </div>

      <div v-if="$slots.actions" class="nx-item-card__actions">
        <slot name="actions" />
      </div>
    </div>

    <div v-if="$slots.media && mediaPlacement === 'end'" class="nx-item-card__media is-end">
      <slot name="media" />
    </div>

    <div v-if="isOpen && $slots.detail" class="nx-item-card__detail">
      <slot name="detail" />
    </div>

    <div v-if="$slots.footer" class="nx-item-card__footer">
      <slot name="footer" />
    </div>
    <NxTooltip
      v-if="look === 'tile'"
      :anchor="root"
      :text="title"
      side="bottom"
      :delay-ms="800"
      interactive
    />
    <span v-if="canDrop" class="nx-item-card__sr" role="status" aria-live="polite">
      {{ dropTarget.label.value }}
    </span>
  </article>
</template>

<style scoped>
/* The strip is the inline-start border. Hover and selection only re-point
   --item-edge (the other three sides), so the strip keeps its colour. */
.nx-item-card {
  --item-edge: var(--nxt-border);
  /* The card's own transitions, in one place: the attention state adds one of its own. */
  --item-transition:
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    box-shadow var(--nxt-motion-fast) var(--nxt-ease-out),
    background-color var(--nxt-motion-fast) var(--nxt-ease-out);
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: var(--nxt-space-sm);
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  background: var(--nxt-surface-raised-bg);
  border: var(--nxt-hairline) solid var(--item-edge);
  border-radius: var(--nxt-radius-md);
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
  text-align: start;
  min-width: 0;
  /* No outline at rest. This is the colour the attention outline fades in from: the
     outline appears when `outline-style` flips, and a colour transition starts from the
     colour before it — `currentcolor` (the text) unless a base colour says otherwise. */
  outline-color: transparent;
  transition: var(--item-transition);
}
.nx-item-card.has-accent {
  border-inline-start: calc(var(--nxt-hairline) * 3 * var(--item-accent-emphasis)) solid
    var(--item-accent);
}
.nx-item-card.is-accent-fill {
  background: color-mix(
    in oklab,
    var(--item-accent) calc(3% + 5% * var(--item-accent-emphasis)),
    var(--nxt-surface-raised-bg)
  );
}
.nx-item-card.is-muted,
.nx-item-card.is-muted .nx-item-card__title {
  color: var(--nxt-fg-muted);
}
.nx-item-card.is-muted {
  filter: saturate(0.7);
}
.nx-item-card.is-interactive {
  cursor: pointer;
}
.nx-item-card.is-selectable {
  user-select: none;
}
.nx-item-card__selection {
  position: absolute;
  inset-block-start: var(--nxt-space-xs);
  inset-inline-end: var(--nxt-space-xs);
  opacity: 0;
}
.nx-item-card:hover .nx-item-card__selection,
.nx-item-card:focus-within .nx-item-card__selection,
.nx-item-card.is-selected .nx-item-card__selection {
  opacity: 1;
}
@media (hover: none), (any-pointer: coarse) {
  .nx-item-card__selection {
    opacity: 1;
  }
}
.nx-item-card.is-interactive:hover {
  --item-edge: var(--nxt-border-strong);
  box-shadow: var(--nxt-shadow-sm);
}
.nx-item-card.is-selected {
  --item-edge: var(--nxt-brand-bg);
  background: var(--nxt-brand-subtle-bg);
}
/* Attention (I-2999): the hint that a jump just landed on this card. An outline,
   so nothing moves and selection, hover and the accent keep their look; it fades
   in from the transparent base colour, and the caller takes it back after a
   moment. Declared before :focus-visible (same specificity), so the keyboard focus
   ring wins where both apply — and that rule sets its own colour, which the
   transparent base does not reach. The transitions are the card's own plus the
   outline's. */
.nx-item-card.is-attention {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-brand-border);
  outline-offset: calc(var(--nxt-space-2xs) * 0.25);
  transition:
    var(--item-transition),
    outline-color var(--nxt-motion-slow);
}
.nx-item-card:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * 2);
}

.nx-item-card__tile {
  flex: none;
  display: grid;
  place-items: center;
  /* The tile's font size is the icon size: a 1em icon fills it, and the em
     radius stays proportional. */
  font-size: var(--nxt-icon-size-lg);
  width: var(--nxt-tile-size-md);
  height: var(--nxt-tile-size-md);
  border-radius: var(--nxt-radius-md);
  background: color-mix(in srgb, var(--nxt-fg) 7%, var(--nxt-surface-raised-bg));
  color: var(--nxt-fg-subtle);
}
.nx-item-card__tile.is-tinted {
  background: var(--item-icon-subtle-bg);
  color: var(--item-icon-fg);
  box-shadow: inset 0 0 0 var(--nxt-hairline) var(--item-icon-border);
}

.nx-item-card__main {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
}

.nx-item-card__head-row {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
}
.nx-item-card__head {
  flex: 1 1 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-2xs) var(--nxt-space-xs);
  min-width: 0;
}
.nx-item-card__unread {
  flex: none;
  width: var(--nxt-dot-size-md);
  height: var(--nxt-dot-size-md);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-bg);
}
.nx-item-card__title {
  min-width: 0;
  overflow-wrap: anywhere;
  font-size: var(--nxt-font-size-md);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: var(--nxt-line-height-tight);
  color: var(--nxt-heading-fg);
}
.nx-item-card__badges {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-2xs);
}
.nx-item-card__aside {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  margin-inline-start: auto;
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
  white-space: nowrap;
}
.nx-item-card__reference {
  font-family: var(--nxt-font-mono);
}
.nx-item-card__due {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  font-variant-numeric: tabular-nums;
}
.nx-item-card__due.is-overdue {
  color: var(--nxt-danger-bg);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-item-card__due-icon {
  flex: none;
}

.nx-item-card__meta {
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
  overflow-wrap: anywhere;
}
.nx-item-card__text {
  color: var(--nxt-text-body-fg);
  overflow-wrap: anywhere;
}
.nx-item-card__text.is-clamped {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}
.nx-item-card__document {
  display: flex;
  flex-wrap: wrap;
  gap: var(--nxt-space-xs);
  margin-block-start: var(--nxt-space-2xs);
}
.nx-item-card__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-xs);
  margin-block-start: var(--nxt-space-xs);
}

/* The „opens“ chevron at the end of the head row, quiet until hover. It sits
   beside the wrapping head, so it never drops to a line of its own and the
   meta line below keeps the full width. */
.nx-item-card__chevron {
  flex: none;
  color: var(--nxt-fg-muted);
}
.nx-item-card.is-interactive:hover .nx-item-card__chevron {
  color: var(--nxt-fg-subtle);
}

/* The footer takes a line of its own across tile and text. */
.nx-item-card__footer {
  flex: 1 0 100%;
  min-width: 0;
}

/* Expandable (K1, I-2382). The eyebrow row: the caption gives way first
   (basis 0, it only grows into free space up to its text), the aside after it. */
.nx-item-card__eyebrow-row {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
}
.nx-item-card__eyebrow {
  flex: 1000 1 0%;
  max-width: max-content;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
}
.nx-item-card__spacer {
  flex: 1;
}
.nx-item-card__slot-aside {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
}
/* The disclosure title: a plain button around the chevron and the title. */
.nx-item-card__toggle {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
  max-width: 100%;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  font-size: var(--nxt-font-size-md);
  font-weight: var(--nxt-font-weight-semibold);
  text-align: start;
  cursor: pointer;
}
.nx-item-card__toggle:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * 2);
  border-radius: var(--nxt-radius-sm);
}
.nx-item-card__toggle-chevron {
  flex: none;
  color: var(--nxt-fg-muted);
  transition: transform var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-item-card__toggle-chevron.is-open {
  transform: rotate(90deg);
}
.nx-item-card__title-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
/* Collapsed: the title stays on one line; open, it wraps. The list look keeps
   its title on one line too, beside the aside. */
.nx-item-card.is-expandable:not(.is-expanded) .nx-item-card__title-text,
.nx-item-card.look-list .nx-item-card__title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-item-card.is-expandable .nx-item-card__head,
.nx-item-card.look-list .nx-item-card__head {
  flex-wrap: nowrap;
}
/* Head actions: revealed on hover, focus, open or selected; always on touch. */
.nx-item-card__head-actions {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity var(--nxt-motion-fast);
}
.nx-item-card:hover .nx-item-card__head-actions,
.nx-item-card:focus-within .nx-item-card__head-actions,
.nx-item-card.is-expanded .nx-item-card__head-actions,
.nx-item-card.is-selected .nx-item-card__head-actions,
.nx-item-card__head-actions.is-visible {
  opacity: 1;
}
/* `headActionsSize="compact"`: a zero-height box centred on the row — the buttons
   overflow it evenly, so they never set the row's height. */
.nx-item-card__head-actions.is-compact {
  height: 0;
}
@media (hover: none), (any-pointer: coarse) {
  .nx-item-card__head-actions {
    opacity: 1;
  }
}
.nx-item-card__media {
  min-width: 0;
  max-width: 100%;
  margin-block-start: var(--nxt-space-2xs);
}
/* Beside the text: a column of its own at the end of the card. */
.nx-item-card__media.is-end {
  flex: none;
  max-width: 40%;
  margin-block-start: 0;
}
.nx-item-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--nxt-space-xs);
  min-width: 0;
}
/* The detail takes a line of its own across tile and text, like the footer. */
.nx-item-card__detail {
  flex: 1 0 100%;
  min-width: 0;
}

/* look="timeline" / "list" (K1 variant B, the B-DMS cards). The strip is a
   pseudo element: grey at rest, the accent when one is set, brand on hover,
   open or selected (timeline without an accent). */
.nx-item-card.look-timeline,
.nx-item-card.look-list {
  gap: var(--nxt-space-2xs) var(--nxt-space-sm);
  background: var(--nxt-surface-bg);
}
.nx-item-card.look-timeline {
  overflow: visible;
}
/* The list card clips what does not fit its column (the aside never wraps);
   menus of controls inside open in the top layer and are not clipped. */
.nx-item-card.look-list {
  overflow: hidden;
}
.nx-item-card.look-timeline.has-accent,
.nx-item-card.look-list.has-accent {
  border-inline-start: var(--nxt-hairline) solid var(--item-edge);
}
.nx-item-card.look-timeline::before,
.nx-item-card.look-list::before {
  content: '';
  position: absolute;
  inset-block: 0;
  inset-inline-start: 0;
  width: calc(var(--nxt-hairline) * 3);
  background: var(--item-accent, var(--nxt-border-strong));
  border-start-start-radius: var(--nxt-radius-sm);
  border-end-start-radius: var(--nxt-radius-sm);
  transition: background var(--nxt-motion-fast);
}
.nx-item-card.look-timeline {
  --item-edge: transparent;
}
.nx-item-card.look-timeline.is-interactive:hover {
  --item-edge: var(--nxt-border);
  box-shadow: var(--nxt-shadow-md);
}
.nx-item-card.look-timeline.is-expanded {
  --item-edge: var(--nxt-border);
  box-shadow: var(--nxt-shadow-sm);
}
.nx-item-card.look-timeline.is-pinned,
.nx-item-card.look-timeline.is-pinned:hover {
  /* Pinned open (a reading pane): the card lies flat on its pane. */
  --item-edge: var(--nxt-border);
  box-shadow: none;
}
.nx-item-card.look-timeline:not(.has-accent).is-interactive:hover::before,
.nx-item-card.look-timeline:not(.has-accent).is-expanded::before,
.nx-item-card.look-timeline:not(.has-accent).is-selected::before {
  background: var(--nxt-brand-bg);
}
.nx-item-card.look-list.is-interactive:hover {
  --item-edge: var(--nxt-border);
  box-shadow: none;
}
/* Selection as everywhere in the kit (NxItemCard, NxBoard): the brand edge and
   the subtle brand fill, one brand colour, no ring — also while hovered. */
.nx-item-card.look-timeline.is-selected,
.nx-item-card.look-timeline.is-selected:hover,
.nx-item-card.look-list.is-selected,
.nx-item-card.look-list.is-selected:hover {
  --item-edge: var(--nxt-brand-bg);
  background: var(--nxt-brand-subtle-bg);
}
/* The list card with an accent (a status) shows its selection in that colour: the
   edge all round and a light wash of it, so the strip and the edge never clash
   (owner decision on #2382, 12.10.). */
.nx-item-card.look-list.has-accent.is-selected,
.nx-item-card.look-list.has-accent.is-selected:hover {
  --item-edge: var(--item-accent);
  background: color-mix(in oklab, var(--item-accent) 5%, var(--nxt-surface-bg));
}
.nx-item-card.look-timeline .nx-item-card__title,
.nx-item-card.look-list .nx-item-card__title {
  font-size: var(--nxt-font-size-sm);
}

/* Grid tiles give the icon, name and markers separate rows. */
.nx-item-card.look-tile {
  box-sizing: border-box;
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: center;
  padding: var(--nxt-space-sm);
  border-color: var(--nxt-border);
  background: color-mix(in srgb, var(--nxt-surface-raised-bg) 60%, transparent);
  text-align: center;
}
.nx-item-card.look-tile.is-interactive:hover {
  border-color: var(--nxt-border-strong);
  background: var(--nxt-surface-sunken-bg);
  box-shadow: none;
}
.nx-item-card.look-tile.is-selected,
.nx-item-card.look-tile.is-selected:hover {
  border-color: var(--nxt-brand-border);
  background: var(--nxt-brand-subtle-bg);
}
.nx-item-card.is-drop-target,
.nx-item-card.look-tile.is-drop-target:hover {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-brand-border);
  outline-offset: calc(var(--nxt-hairline) * -2);
  background: var(--nxt-brand-subtle-bg);
}
.nx-item-card.look-tile .nx-item-card__tile {
  width: var(--nxt-area-size-xs);
  height: var(--nxt-area-size-xs);
  font-size: var(--nxt-icon-size-2xl);
}
.nx-item-card.look-tile .nx-item-card__tile:not(.is-tinted) {
  background: transparent;
}
.nx-item-card.look-tile .nx-item-card__main {
  flex: 1 1 auto;
  width: 100%;
  gap: var(--nxt-space-2xs);
}
.nx-item-card.look-tile .nx-item-card__head-row {
  align-items: flex-start;
}
.nx-item-card.look-tile .nx-item-card__head {
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: center;
  width: 100%;
  gap: var(--nxt-space-2xs);
}
.nx-item-card.look-tile .nx-item-card__title {
  align-self: stretch;
  max-width: 100%;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
}
.nx-item-card.look-tile .nx-item-card__badges {
  justify-content: center;
}
.nx-item-card.look-tile .nx-item-card__footer,
.nx-item-card.look-tile .nx-item-card__detail {
  flex: 0 0 auto;
}

/* Compact: bell entries and chat embeds. */
.nx-item-card.is-compact {
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
}
.nx-item-card.is-compact .nx-item-card__tile {
  font-size: var(--nxt-icon-size-md);
  width: var(--nxt-tile-size-sm);
  height: var(--nxt-tile-size-sm);
}
.nx-item-card.is-compact .nx-item-card__title {
  font-size: var(--nxt-font-size-sm);
}
.nx-item-card.is-compact .nx-item-card__text {
  font-size: var(--nxt-font-size-xs);
}
.nx-item-card.is-compact .nx-item-card__actions {
  margin-block-start: var(--nxt-space-2xs);
}

/* Screen-reader-only text (unread, overdue). */
.nx-item-card__sr {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  margin: calc(var(--nxt-hairline) * -1);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

@media (prefers-reduced-motion: reduce) {
  .nx-item-card,
  .nx-item-card.is-attention,
  .nx-item-card::before,
  .nx-item-card__toggle-chevron,
  .nx-item-card__head-actions {
    transition: none;
  }
}
@media (forced-colors: active) {
  .nx-item-card.is-selected {
    outline: calc(var(--nxt-hairline) * 2) solid Highlight;
  }
}
</style>
